import { TimesheetColumnDef, TimeSheetData } from '@interfaces/vendor-management-model';
import { caseInsensitiveStringComparator } from '@utils/utils';

// Configuration constants
export const CONFIG_CONSTANTS = {
  HEADERS: {
    SELECT: '',
    EMPLOYEE_ID: 'Employee ID',
    NAME: 'Name',
    EMPLOYEE_STATUS: 'Emp. Status',
    VENDOR: 'Vendor',
    DNT_LEADER: 'D&T Leader',
    REPORTING_MANAGER: 'Reporting Mgr',
    LOCATION: 'Location',
    WORKING_HRS: 'Working Hrs',
    BILLABLE_HRS: 'Billable',
    LEAVES_AVAILED: 'Leaves',
    STATUS: 'Status',
    ACTION: 'Action',
    L3_MANAGER: 'L3 Manager',
    L4_MANAGER: 'L4 Manager',
    L5_MANAGER: 'L5 Manager',
    PAID_LEAVES: 'Paid Leaves',
    UNPAID_LEAVES: 'Unpaid Leaves',
    LAST_WORKING_DAY: 'Last Working Day',
    REVIEWED_BY: 'Approved By',
    REVIEWED_DATE: 'Approved Date',
  },
  FIELDS: {
    SELECT: 'select',
    EMPLOYEE_ID: 'employeeId',
    EMPLOYEE_NAME: 'employeeName',
     EMPLOYEE_STATUS: 'employeeStatus',
    VENDOR_NAME: 'vendorName',
    DNT_LEADER: 'l3ManagerName',
    REPORTING_MANAGER: 'reportingManager',
    LOCATION: 'location',
    WORKING_HRS: 'workingHrs',
    BILLABLE_HRS: 'billableHrs',
    LEAVES_AVAILED: 'leavesAvailed',
    STATUS: 'status',
    ACTION: 'action',
    L3_MANAGER_NAME: 'l3ManagerName',
    L4_MANAGER_NAME: 'l4ManagerName',
    L5_MANAGER_NAME: 'l5ManagerName',
    PAID_LEAVES: 'paidLeaves',
    UNPAID_LEAVES: 'unpaidLeaves',
    LAST_WORKING_DAY: 'lastWorkingDay',
    REVIEWED_BY: 'reviewedBy',
    REVIEWED_DATE: 'reviewedDate',

  },
  CELL_TEMPLATES: {
    CUSTOM_SELECT_COLUMN: 'customSelectColumn'
  }
} as const;

// Reusable configuration objects
const FILTER_CONFIG = { hideMenu: true };
const SORT_ENABLED_CONFIG = { enabled: true };
const SORT_DISABLED_CONFIG = { enabled: false };

const baseColumnDefs: TimesheetColumnDef[] = [
  {
    field: CONFIG_CONSTANTS.FIELDS.EMPLOYEE_NAME,
    headerName: 'Employee',
      filterParams: FILTER_CONFIG,
  sortParams: SORT_ENABLED_CONFIG,
  sortComparator: caseInsensitiveStringComparator,
},
  {
    field: CONFIG_CONSTANTS.FIELDS.VENDOR_NAME,
    headerName: CONFIG_CONSTANTS.HEADERS.VENDOR,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.REPORTING_MANAGER,
    headerName: CONFIG_CONSTANTS.HEADERS.REPORTING_MANAGER,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.WORKING_HRS,
    headerName: CONFIG_CONSTANTS.HEADERS.WORKING_HRS,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.LEAVES_AVAILED,
    headerName: CONFIG_CONSTANTS.HEADERS.LEAVES_AVAILED,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.BILLABLE_HRS,
    headerName: CONFIG_CONSTANTS.HEADERS.BILLABLE_HRS,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.STATUS,
    headerName: CONFIG_CONSTANTS.HEADERS.STATUS,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.ACTION,
    headerName: CONFIG_CONSTANTS.HEADERS.ACTION,
    filterParams: FILTER_CONFIG,
  },
  // pushed past Action so the frequently-used columns fit without scrolling right
  {
    field: CONFIG_CONSTANTS.FIELDS.DNT_LEADER,
    headerName: CONFIG_CONSTANTS.HEADERS.DNT_LEADER,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.EMPLOYEE_STATUS,
    headerName: CONFIG_CONSTANTS.HEADERS.EMPLOYEE_STATUS,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.LOCATION,
    headerName: CONFIG_CONSTANTS.HEADERS.LOCATION,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
  },
];

export const getColumnDefs = (rowData: TimeSheetData[] = []): TimesheetColumnDef[] => {
  const selectColumn = {
    field: CONFIG_CONSTANTS.FIELDS.SELECT,
    dataType: CONFIG_CONSTANTS.FIELDS.SELECT,
    headerName: CONFIG_CONSTANTS.HEADERS.SELECT,
    cellRenderer: null,
    checkboxSelection: false,
    customCellTemplate: CONFIG_CONSTANTS.CELL_TEMPLATES.CUSTOM_SELECT_COLUMN,
    hide: false,
  };

  return [selectColumn, ...baseColumnDefs];
};

export const exportColumnDefs = [
  {
    field: CONFIG_CONSTANTS.FIELDS.VENDOR_NAME,
    headerName: CONFIG_CONSTANTS.HEADERS.VENDOR,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.EMPLOYEE_ID,
    headerName: CONFIG_CONSTANTS.HEADERS.EMPLOYEE_ID,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.EMPLOYEE_NAME,
    headerName: CONFIG_CONSTANTS.HEADERS.NAME,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.REPORTING_MANAGER,
    headerName: CONFIG_CONSTANTS.HEADERS.REPORTING_MANAGER,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.WORKING_HRS,
    headerName: CONFIG_CONSTANTS.HEADERS.WORKING_HRS,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.LEAVES_AVAILED,
    headerName: CONFIG_CONSTANTS.HEADERS.LEAVES_AVAILED,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.PAID_LEAVES,
    headerName: CONFIG_CONSTANTS.HEADERS.PAID_LEAVES,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.UNPAID_LEAVES,
    headerName: CONFIG_CONSTANTS.HEADERS.UNPAID_LEAVES,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.BILLABLE_HRS,
    headerName: CONFIG_CONSTANTS.HEADERS.BILLABLE_HRS,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.STATUS,
    headerName: CONFIG_CONSTANTS.HEADERS.STATUS,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.ACTION,
    headerName: CONFIG_CONSTANTS.HEADERS.ACTION,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.L3_MANAGER_NAME,
    headerName: CONFIG_CONSTANTS.HEADERS.L3_MANAGER,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.L4_MANAGER_NAME,
    headerName: CONFIG_CONSTANTS.HEADERS.L4_MANAGER,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.L5_MANAGER_NAME,
    headerName: CONFIG_CONSTANTS.HEADERS.L5_MANAGER,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.LOCATION,
    headerName: CONFIG_CONSTANTS.HEADERS.LOCATION,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.LAST_WORKING_DAY,
    headerName: CONFIG_CONSTANTS.HEADERS.LAST_WORKING_DAY,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.REVIEWED_BY,
    headerName: CONFIG_CONSTANTS.HEADERS.REVIEWED_BY,
  },
  {
    field: CONFIG_CONSTANTS.FIELDS.REVIEWED_DATE,
    headerName: CONFIG_CONSTANTS.HEADERS.REVIEWED_DATE,
  },
  {
    field: 'submittedBy',
    headerName: 'Submitted By',
  },
  {
    field: 'submittedDate',
    headerName: 'Submitted Date',
  },
];

export const icon_fontSize = 1.3;
